import { useImperativeHandle, useState } from "react";

import {
  FieldDefinitionDateType,
  FieldDefinitionFieldType,
  FieldImperativeHandle,
} from "@/components/@types";
import { Popper } from "@/components/atoms/Popper";
import { DatePicker } from "@/components/organisms/DatePicker";
import { FieldTypography } from "@/components/organisms/Field/FieldTypography";
import { Utils } from "@/utils";

/**
 * A date, picked from a calendar rather than typed: typed into, the box read
 * a date back after every key, and emptied at the first one that was not one.
 * It is kept as the day alone ("2026-03-01"), which reads as that day wherever
 * it is opened; a day and time given it - a day the server keeps as its
 * midnight in UTC - is the day it reads as, its day in UTC.
 */
export function FieldDate({
  value,
  events,
  ref,
  ...props
}: FieldDefinitionFieldType<FieldDefinitionDateType<any>> & {
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const text = typeof value === "string" ? value : "";
  // The day it reads as, as ``fDate`` prints it - its day in UTC - at the
  // picker's own midnight, so the calendar opens on the day the field says
  // and not the evening before west of Greenwich.
  const at = text ? new Date(text) : null;
  const picked = at
    ? new Date(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate())
    : undefined;

  useImperativeHandle(
    ref,
    () => ({
      setFocus: () => setIsOpen(true),
      unsetFocus: () => setIsOpen(false),
    }),
    [],
  );

  return (
    <Popper
      isOpenState={[isOpen, setIsOpen]}
      placement="bottom-start"
      // A block, as the day read alone is: inline, it sat on a line of the
      // page's own taller text, and its day was lower to pick than to read.
      style={{ display: "block" }}
      anchor={
        <div
          role="button"
          tabIndex={0}
          data-testid="field-date"
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setIsOpen(true);
            }
          }}
          className="cursor-pointer min-w-0"
        >
          <FieldTypography
            {...(props as FieldDefinitionFieldType<never>)}
            value={text ? Utils.fDate(new Date(text)) : ""}
            mode="date"
          />
        </div>
      }
    >
      <DatePicker
        date1={picked}
        setDate1={(date) => {
          const day = [
            date.getFullYear(),
            String(date.getMonth() + 1).padStart(2, "0"),
            String(date.getDate()).padStart(2, "0"),
          ].join("-");
          events?.onChangeField?.(day);
        }}
        onChange={() => {
          setIsOpen(false);
          events?.onSubmitField?.();
        }}
      />
    </Popper>
  );
}
